import { useAuthStore } from '@/store/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.facetech.my'
const CLIENT_KEY = import.meta.env.VITE_CLIENT_KEY || 'client_mcvfwllv_ca130d82'

interface ApiOptions extends RequestInit {
  requiresAuth?: boolean
}

class ApiService {
  private getHeaders(options: ApiOptions = {}): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (options.requiresAuth !== false) {
      const { token, clientKey } = useAuthStore.getState()

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Use dynamic client key if available, otherwise fall back to env variable
      const activeClientKey = clientKey || CLIENT_KEY
      if (activeClientKey) {
        headers['X-Client-Key'] = activeClientKey
      }
    } else {
      // For non-authenticated requests, use the portal's client key from env
      headers['X-Client-Key'] = CLIENT_KEY
    }

    return {
      ...headers,
      ...options.headers,
    }
  }

  async request(endpoint: string, options: ApiOptions = {}): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`
    const config: RequestInit = {
      ...options,
      headers: this.getHeaders(options),
    }

    const response = await fetch(url, config)

    // Handle 401 Unauthorized - logout user
    if (response.status === 401 && options.requiresAuth !== false) {
      useAuthStore.getState().logout()
      throw new Error('Authentication required')
    }

    return response
  }

  async get(endpoint: string, options: ApiOptions = {}): Promise<Response> {
    return this.request(endpoint, { ...options, method: 'GET' })
  }

  async post(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<Response> {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<Response> {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete(endpoint: string, options: ApiOptions = {}): Promise<Response> {
    return this.request(endpoint, { ...options, method: 'DELETE' })
  }

  async patch(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<Response> {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }
}

export const apiService = new ApiService()
export default apiService